import type { RowsPerPageOption } from '@ovComponents/2-component/dataTable';
import { UserContext } from '@providers/userContextProvider';
import { usePageState } from '@utils/usePageState';
import type { Theme } from 'hephaestus/core';
import type { TFunction } from 'i18next';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type UseActivitiesMetricsResult,
  useActivitiesMetrics,
} from '../dashboard/useActivitiesMetrics';
import type { ActivitiesFiltersCardProps } from '../filters/activitiesFiltersCard.types';
import type { ActivitiesTableCardProps } from '../table/activitiesTableCard';
import { ACTIVITIES_PAGE_SIZE } from '../utils/activities.config';
import type { ActivitiesGridActivity } from '../utils/activities.queries';
import { useActivitiesFilters } from './useActivitiesFilters';
import { useActivitiesGrid } from './useActivitiesGrid';

export type UseReconActivitiesPageResult = {
  t: TFunction;
  theme: Theme;
  metrics: UseActivitiesMetricsResult;
  filtersProps: ActivitiesFiltersCardProps;
  gridProps: ActivitiesTableCardProps;
};

export const useReconActivitiesPage = (
  enabled = true,
  theme: Theme,
): UseReconActivitiesPageResult => {
  const { t } = useTranslation(['reconciliation']);
  const { activeOrganization } = useContext(UserContext);
  const orgId = activeOrganization?.id;
  const metrics = useActivitiesMetrics(orgId, enabled);

  const [page, setPage] = usePageState(1, 'raPage');
  const [pageSizeStored, setPageSizeStored] = usePageState(
    ACTIVITIES_PAGE_SIZE,
    'raPageSize',
  );
  const pageSize = pageSizeStored as RowsPerPageOption;
  const setPageSize = useCallback(
    (size: RowsPerPageOption) => setPageSizeStored(size),
    [setPageSizeStored],
  );

  const [expandedActivityIds, setExpandedActivityIds] = useState<Set<string>>(
    () => new Set(),
  );

  const onToggleExpand = useCallback((activityId: string) => {
    setExpandedActivityIds((prev) => {
      const next = new Set(prev);
      if (next.has(activityId)) {
        next.delete(activityId);
      } else {
        next.add(activityId);
      }
      return next;
    });
  }, []);

  const onRowClick = useCallback(
    (row: ActivitiesGridActivity) => {
      onToggleExpand(row.id);
    },
    [onToggleExpand],
  );

  const { gridFilter, state, filtersBaseProps } = useActivitiesFilters({
    orgId,
    setPage,
  });

  const { gridProps, visibleActivityIds } = useActivitiesGrid({
    enabled,
    theme,
    t,
    orgId,
    gridFilter,
    state,
    page,
    setPage,
    pageSize,
    setPageSize,
    expandedActivityIds,
    onToggleExpand,
    onRowClick,
  });

  const allVisibleExpanded = useMemo(
    () =>
      visibleActivityIds.length > 0 &&
      visibleActivityIds.every((id) => expandedActivityIds.has(id)),
    [expandedActivityIds, visibleActivityIds],
  );

  const onToggleExpandAll = useCallback(() => {
    setExpandedActivityIds((prev) => {
      const next = new Set(prev);
      const shouldCollapse =
        visibleActivityIds.length > 0 &&
        visibleActivityIds.every((id) => next.has(id));
      if (shouldCollapse) {
        for (const id of visibleActivityIds) {
          next.delete(id);
        }
      } else {
        for (const id of visibleActivityIds) {
          next.add(id);
        }
      }
      return next;
    });
  }, [visibleActivityIds]);

  return {
    t,
    theme,
    metrics,
    filtersProps: {
      ...filtersBaseProps,
      allVisibleExpanded,
      onToggleExpandAll,
      expandAllDisabled: visibleActivityIds.length === 0,
    },
    gridProps: { ...gridProps, theme },
  };
};
